import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { COMPANY } from "@/lib/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTERESTS = [
  "Owner's Representation",
  "Risk Management Consulting",
  "Real Estate Development Advisory",
  "Project Oversight",
  "Due Diligence",
  "Insurance & Risk Strategy",
  "General Inquiry",
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      setForm({ name: "", email: "", company: "", phone: "", interest: "", message: "" });
      toast.success("Inquiry received. We'll be in touch shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not submit. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#F8F9FA] py-24 md:py-32"
    >
      <div className="max-w-[1320px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left: contact info */}
          <div className="lg:col-span-5">
            <div className="ra-eyebrow mb-6">
              <span className="ra-hairline" />
              <span>Contact</span>
            </div>
            <h2 className="font-heading text-[#0A1128] text-3xl md:text-4xl lg:text-[52px] font-medium leading-[1.08] tracking-tight">
              Begin a Confidential Conversation.
            </h2>
            <p className="font-body text-[#4B5563] text-base md:text-[17px] leading-relaxed mt-6 max-w-md">
              Tell us about your project, opportunity, or area of concern. We
              respond personally — typically within one business day.
            </p>

            <div className="mt-12 space-y-7">
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                data-testid="contact-phone"
                className="flex items-start gap-4 group"
              >
                <div className="w-11 h-11 bg-[#0A1128] text-[#00A859] flex items-center justify-center shrink-0">
                  <Phone size={18} strokeWidth={1.8} />
                </div>
                <div>
                  <div className="font-heading text-[10px] tracking-[0.3em] text-[#4B5563] uppercase mb-1">
                    Direct Line
                  </div>
                  <div className="font-heading text-[#0A1128] text-xl md:text-2xl font-medium group-hover:text-[#00A859] transition-colors">
                    {COMPANY.phone}
                  </div>
                </div>
              </a>

              <a
                href={`mailto:${COMPANY.email}`}
                data-testid="contact-email"
                className="flex items-start gap-4 group"
              >
                <div className="w-11 h-11 bg-[#0A1128] text-[#00A859] flex items-center justify-center shrink-0">
                  <Mail size={18} strokeWidth={1.8} />
                </div>
                <div>
                  <div className="font-heading text-[10px] tracking-[0.3em] text-[#4B5563] uppercase mb-1">
                    Email
                  </div>
                  <div className="font-heading text-[#0A1128] text-xl md:text-2xl font-medium group-hover:text-[#00A859] transition-colors break-all">
                    {COMPANY.email}
                  </div>
                </div>
              </a>

              <div className="flex items-start gap-4" data-testid="contact-service-area">
                <div className="w-11 h-11 bg-[#0A1128] text-[#00A859] flex items-center justify-center shrink-0">
                  <MapPin size={18} strokeWidth={1.8} />
                </div>
                <div>
                  <div className="font-heading text-[10px] tracking-[0.3em] text-[#4B5563] uppercase mb-1">
                    Service Area
                  </div>
                  <div className="font-body text-[#0A1128] text-base leading-relaxed">
                    New Jersey · New York
                    <br />
                    Remote Consulting Nationwide
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-[#E5E7EB] p-8 md:p-12">
              {success ? (
                <div
                  data-testid="contact-success-state"
                  className="py-10 text-center"
                >
                  <div className="w-14 h-14 mx-auto bg-[#ECFBF3] text-[#00A859] flex items-center justify-center mb-6">
                    <CheckCircle2 size={28} strokeWidth={1.6} />
                  </div>
                  <h3 className="font-heading text-[#0A1128] text-2xl md:text-3xl font-medium mb-4">
                    Inquiry Received.
                  </h3>
                  <p className="font-body text-[#4B5563] max-w-md mx-auto">
                    Thank you for reaching out. A member of the Ramo Advisors
                    team will respond personally within one business day.
                  </p>
                  <button
                    data-testid="contact-new-inquiry"
                    onClick={() => setSuccess(false)}
                    className="ra-btn-ghost mt-8"
                  >
                    Send another inquiry <ArrowRight size={14} />
                  </button>
                </div>
              ) : (
                <form
                  data-testid="contact-form"
                  onSubmit={onSubmit}
                  className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2"
                >
                  <div className="md:col-span-2 mb-2">
                    <div className="font-heading text-[10px] tracking-[0.3em] text-[#00A859] uppercase mb-2">
                      Inquiry Form
                    </div>
                    <h3 className="font-heading text-[#0A1128] text-2xl md:text-3xl font-medium">
                      Tell us about your project.
                    </h3>
                  </div>

                  <div>
                    <label
                      htmlFor="name"
                      className="block font-heading text-[10px] tracking-[0.3em] text-[#4B5563] uppercase mb-1"
                    >
                      Full Name *
                    </label>
                    <input
                      data-testid="contact-input-name"
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={onChange}
                      className="ra-input"
                      placeholder="Jane Smith"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block font-heading text-[10px] tracking-[0.3em] text-[#4B5563] uppercase mb-1"
                    >
                      Email *
                    </label>
                    <input
                      data-testid="contact-input-email"
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={onChange}
                      className="ra-input"
                      placeholder="jane@firm.com"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="company"
                      className="block font-heading text-[10px] tracking-[0.3em] text-[#4B5563] uppercase mb-1"
                    >
                      Company
                    </label>
                    <input
                      data-testid="contact-input-company"
                      id="company"
                      name="company"
                      type="text"
                      value={form.company}
                      onChange={onChange}
                      className="ra-input"
                      placeholder="Capital Partners LLC"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="phone"
                      className="block font-heading text-[10px] tracking-[0.3em] text-[#4B5563] uppercase mb-1"
                    >
                      Phone
                    </label>
                    <input
                      data-testid="contact-input-phone"
                      id="phone"
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={onChange}
                      className="ra-input"
                      placeholder="(555) 555-5555"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label
                      htmlFor="interest"
                      className="block font-heading text-[10px] tracking-[0.3em] text-[#4B5563] uppercase mb-1"
                    >
                      Area of Interest
                    </label>
                    <select
                      data-testid="contact-input-interest"
                      id="interest"
                      name="interest"
                      value={form.interest}
                      onChange={onChange}
                      className="ra-input"
                    >
                      <option value="">Select a service…</option>
                      {INTERESTS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2 mt-2">
                    <label
                      htmlFor="message"
                      className="block font-heading text-[10px] tracking-[0.3em] text-[#4B5563] uppercase mb-1"
                    >
                      How Can We Help? *
                    </label>
                    <textarea
                      data-testid="contact-input-message"
                      id="message"
                      name="message"
                      required
                      rows={5}
                      value={form.message}
                      onChange={onChange}
                      className="ra-input resize-none"
                      placeholder="Briefly describe your project, opportunity, or area of concern…"
                    />
                  </div>

                  <div className="md:col-span-2 mt-8 flex items-center justify-between flex-wrap gap-4">
                    <p className="font-body text-[12px] text-[#4B5563] max-w-xs">
                      Submissions are confidential. We respond within one
                      business day.
                    </p>
                    <button
                      data-testid="contact-submit"
                      type="submit"
                      disabled={submitting}
                      className="ra-btn-primary disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Sending
                        </>
                      ) : (
                        <>
                          Send Inquiry <ArrowRight size={16} />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
